/**
 * Android Navigation Bar Utilities
 * Handles Android navigation bar issues and ensures payment buttons are always visible
 */

class AndroidNavbarUtils {
    constructor() {
        this.isAndroid = this.detectAndroid();
        this.navbarHeight = 0;
        this.init();
    }

    /**
     * Detect if the device is running Android
     */
    detectAndroid() {
        return /Android/i.test(navigator.userAgent);
    }

    /**
     * Initialize the utility
     */
    init() {
        if (this.isAndroid) {
            this.setupEventListeners();
            this.adjustForNavbar();
            this.ensureButtonVisibility();
        }
    }

    /**
     * Set up event listeners for orientation and resize changes
     */
    setupEventListeners() {
        // Handle orientation changes
        window.addEventListener('orientationchange', () => {
            setTimeout(() => {
                this.adjustForNavbar();
                this.ensureButtonVisibility();
            }, 100);
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            this.adjustForNavbar();
            this.ensureButtonVisibility();
        });

        // Handle viewport changes
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => {
                this.adjustForNavbar();
                this.ensureButtonVisibility();
            });
        }

        // Handle scroll events to ensure button visibility
        window.addEventListener('scroll', () => {
            this.ensureButtonVisibility();
        });
    }

    /**
     * Adjust layout for Android navigation bar
     */
    adjustForNavbar() {
        const actionBars = document.querySelectorAll('.bottom-action-bar');
        const containers = document.querySelectorAll('.mobile-container');
        
        // Calculate safe area insets
        const safeAreaBottom = this.getSafeAreaBottom();
        
        actionBars.forEach(bar => {
            if (bar) {
                bar.style.paddingBottom = `calc(1rem + ${safeAreaBottom}px)`;
                bar.style.marginBottom = `${safeAreaBottom}px`;
            }
        });

        containers.forEach(container => {
            if (container) {
                container.style.paddingBottom = `${safeAreaBottom}px`;
            }
        });
    }

    /**
     * Get the safe area bottom value
     */
    getSafeAreaBottom() {
        // Use CSS env() if supported
        if (CSS.supports('padding', 'env(safe-area-inset-bottom)')) {
            const testElement = document.createElement('div');
            testElement.style.position = 'absolute';
            testElement.style.visibility = 'hidden';
            testElement.style.paddingBottom = 'env(safe-area-inset-bottom)';
            document.body.appendChild(testElement);
            
            const computedStyle = window.getComputedStyle(testElement);
            const safeArea = parseInt(computedStyle.paddingBottom) || 0;
            
            document.body.removeChild(testElement);
            return safeArea;
        }

        // Fallback for older browsers
        return this.calculateNavbarHeight();
    }

    /**
     * Calculate navigation bar height for older browsers
     */
    calculateNavbarHeight() {
        const viewportHeight = window.innerHeight;
        const screenHeight = window.screen.height;
        const navbarHeight = screenHeight - viewportHeight;
        
        // Return a reasonable default if calculation fails
        return Math.max(navbarHeight, 20);
    }

    /**
     * Ensure payment buttons are always visible
     */
    ensureButtonVisibility() {
        const paymentButtons = document.querySelectorAll('.payment-button');
        const actionBars = document.querySelectorAll('.bottom-action-bar');
        
        paymentButtons.forEach(button => {
            if (button) {
                // Ensure button is in viewport
                this.ensureButtonInViewport(button);
                
                // Add visual feedback for better UX
                this.addButtonFeedback(button);
            }
        });

        actionBars.forEach(bar => {
            if (bar) {
                // Ensure action bar is always visible
                this.ensureActionBarVisible(bar);
            }
        });
    }

    /**
     * Ensure button is in viewport
     */
    ensureButtonInViewport(button) {
        const rect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        // If button is below viewport, scroll to it
        if (rect.bottom > viewportHeight) {
            button.scrollIntoView({
                behavior: 'smooth',
                block: 'end'
            });
        }
    }

    /**
     * Ensure action bar is always visible
     */
    ensureActionBarVisible(bar) {
        // Add sticky positioning if not already present
        if (!bar.style.position || bar.style.position !== 'sticky') {
            bar.style.position = 'sticky';
            bar.style.bottom = '0';
            bar.style.zIndex = '1000';
        }
    }

    /**
     * Add visual feedback to buttons
     */
    addButtonFeedback(button) {
        // Add ripple effect on touch
        button.addEventListener('touchstart', (e) => {
            this.createRippleEffect(e, button);
        });

        // Add focus styles for accessibility
        button.addEventListener('focus', () => {
            button.style.outline = '2px solid #1680f0';
            button.style.outlineOffset = '2px';
        });

        button.addEventListener('blur', () => {
            button.style.outline = '';
            button.style.outlineOffset = '';
        });
    }

    /**
     * Create ripple effect for better touch feedback
     */
    createRippleEffect(event, button) {
        const ripple = document.createElement('span');
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = event.touches[0].clientX - rect.left - size / 2;
        const y = event.touches[0].clientY - rect.top - size / 2;

        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('ripple');

        // Add ripple styles
        ripple.style.position = 'absolute';
        ripple.style.borderRadius = '50%';
        ripple.style.background = 'rgba(255, 255, 255, 0.3)';
        ripple.style.transform = 'scale(0)';
        ripple.style.animation = 'ripple 0.6s linear';
        ripple.style.pointerEvents = 'none';

        button.style.position = 'relative';
        button.style.overflow = 'hidden';
        button.appendChild(ripple);

        // Remove ripple after animation
        setTimeout(() => {
            if (ripple.parentNode) {
                ripple.parentNode.removeChild(ripple);
            }
        }, 600);
    }

    /**
     * Add CSS animations for ripple effect
     */
    addRippleStyles() {
        if (!document.getElementById('ripple-styles')) {
            const style = document.createElement('style');
            style.id = 'ripple-styles';
            style.textContent = `
                @keyframes ripple {
                    to {
                        transform: scale(4);
                        opacity: 0;
                    }
                }
                
                .ripple {
                    animation: ripple 0.6s linear;
                }
            `;
            document.head.appendChild(style);
        }
    }

    /**
     * Force refresh layout (useful for dynamic content)
     */
    refreshLayout() {
        this.adjustForNavbar();
        this.ensureButtonVisibility();
    }

    /**
     * Get current navigation bar status
     */
    getNavbarStatus() {
        return {
            isAndroid: this.isAndroid,
            navbarHeight: this.navbarHeight,
            safeAreaBottom: this.getSafeAreaBottom(),
            viewportHeight: window.innerHeight,
            screenHeight: window.screen.height
        };
    }
}

// Initialize the utility when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    window.androidNavbarUtils = new AndroidNavbarUtils();
    window.androidNavbarUtils.addRippleStyles();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
    module.exports = AndroidNavbarUtils;
} 